import React, {useCallback, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import type {NoProps} from 'types';
import {Concept} from 'types';
import type {
  FieldError,
  LinkedValueLink,
  UnverifiedLinkedValueLink,
} from 'schemaDefinition/types';
import type {Code} from 'services/codelists/types';
import {Card, Scene, Spacer, Text} from 'components';
import {
  CheckboxField,
  CodeListField,
  DatePickerField,
  FieldLabel,
  FieldValue,
  GroupLabel,
  HtmlField,
  LinkedField,
  RadioButtonsField,
  Select,
  SingleValueListField,
  TextAreaField,
  TextField,
  YearPickerField,
} from 'components/fields';
import {AddButton} from 'components/fields/AddButton';
import type {DateRangeValue} from 'components/fields/DateRangeField';
import {DateRangeField} from 'components/fields/DateRangeField';
import {EmptyListPlaceholder} from 'components/fields/EmptyListPlaceholder';
import {FieldErrorMessage} from 'components/fields/FieldErrorMessage';
import {NullFieldPlaceholder} from 'components/fields/NullFieldPlaceholder';
import {useLinkedValueSearchContextProvider} from 'sceneExtensions';

type Props = NoProps;

/**
 * Demo scene rendering all field components from components/fields in the
 * same set of states, aligned in a grid, to compare styling and look.
 */

const STATES = ['empty', 'value', 'error', 'warning', 'readonly'] as const;
type DemoState = (typeof STATES)[number];

const STATE_TITLES: Record<DemoState, string> = {
  empty: 'Empty (placeholder)',
  value: 'With value',
  error: 'Error',
  warning: 'Warning',
  readonly: 'Readonly',
};

const GRID_COLUMNS = '180px repeat(5, 1fr)';

const getStateProps = (
  state: DemoState,
): {error: FieldError; readonly: boolean} => ({
  error: state === 'error' ? true : state === 'warning' ? 'warning' : false,
  readonly: state === 'readonly',
});

const DEMO_CODES: Code[] = [
  {code: 'nb', value: 'Norsk bokmål'},
  {code: 'nn', value: 'Norsk nynorsk'},
  {code: 'en', value: 'Engelsk'},
  {code: 'se', value: 'Nordsamisk', deactivated: true},
];

const LONG_TEXT =
  'Abid Raja er en norsk politiker (V) og advokat. Høsten 2013 ble han valgt' +
  ' inn på Stortinget for Akershus Venstre.';

const Row: React.FC<{
  title: string;
  render: (state: DemoState) => React.ReactNode;
}> = ({title, render}) => (
  <Box
    sx={theme => ({
      display: 'grid',
      gridTemplateColumns: GRID_COLUMNS,
      columnGap: 2,
      alignItems: 'start',
      py: 1.5,
      borderBottom: `1px solid ${theme.palette.divider}`,
    })}>
    <Text kind="primary" variant="body2">
      {title}
    </Text>
    {STATES.map(state => (
      <Box key={state} sx={{minWidth: 0}}>
        {render(state)}
      </Box>
    ))}
  </Box>
);

const HeaderRow: React.FC = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID_COLUMNS,
      columnGap: 2,
      pb: 1,
    }}>
    <Text kind="primary" variant="h4">
      {'Component'}
    </Text>
    {STATES.map(state => (
      <Text key={state} kind="primary" variant="h4">
        {STATE_TITLES[state]}
      </Text>
    ))}
  </Box>
);

const NotSupported: React.FC = () => (
  <Text kind="primary" variant="body2" sx={{opacity: 0.4}}>
    {'–'}
  </Text>
);

const TextFieldRow: React.FC<{type: 'text' | 'digits'}> = ({type}) => {
  const initial = type === 'digits' ? '9788202098087' : 'Kristin Lavransdatter';
  const [values, setValues] = useState<Record<DemoState, string>>({
    empty: '',
    value: initial,
    error: initial,
    warning: initial,
    readonly: initial,
  });

  return (
    <Row
      title={`TextField (${type})`}
      render={state => (
        <TextField
          name={`textfield-${type}-${state}`}
          label={'Label'}
          placeholder={'Placeholder'}
          type={type}
          value={values[state]}
          {...getStateProps(state)}
          onChange={e =>
            setValues(prev => ({...prev, [state]: e.target.value}))
          }
        />
      )}
    />
  );
};

const TextAreaFieldRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, string>>({
    empty: '',
    value: LONG_TEXT,
    error: LONG_TEXT,
    warning: LONG_TEXT,
    readonly: LONG_TEXT,
  });

  return (
    <Row
      title={'TextAreaField'}
      render={state => (
        <TextAreaField
          name={`textarea-${state}`}
          label={'Label'}
          placeholder={'Placeholder'}
          maxLength={200}
          rows={3}
          value={values[state]}
          {...getStateProps(state)}
          onChange={e =>
            setValues(prev => ({...prev, [state]: e.target.value}))
          }
        />
      )}
    />
  );
};

const HtmlFieldRow: React.FC = () => (
  <Row
    title={'HtmlField'}
    render={state =>
      state === 'value' || state === 'readonly' ? (
        <HtmlField
          name={`html-${state}`}
          label={'Label'}
          readonly
          value={
            '<p>Abid Raja er en <b>norsk</b> politiker og <i>advokat</i>.</p>'
          }
        />
      ) : (
        <NotSupported />
      )
    }
  />
);

const YearPickerFieldRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, number | null>>({
    empty: null,
    value: 2021,
    error: 2021,
    warning: 2021,
    readonly: 2021,
  });

  return (
    <Row
      title={'YearPickerField'}
      render={state => (
        <YearPickerField
          name={`year-${state}`}
          label={'Label'}
          value={values[state]}
          {...getStateProps(state)}
          onChange={value => setValues(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const DatePickerFieldRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, string | null>>({
    empty: null,
    value: '2021-11-15',
    error: '2021-11-15',
    warning: '2021-11-15',
    readonly: '2021-11-15',
  });

  return (
    <Row
      title={'DatePickerField'}
      render={state => (
        <DatePickerField
          name={`date-${state}`}
          label={'Label'}
          value={values[state]}
          {...getStateProps(state)}
          onChange={value => setValues(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const DateRangeFieldRow: React.FC = () => {
  const [values, setValues] = useState<
    Record<DemoState, DateRangeValue | undefined>
  >({
    empty: undefined,
    value: undefined,
    error: undefined,
    warning: undefined,
    readonly: undefined,
  });

  return (
    <Row
      title={'DateRangeField'}
      render={state =>
        state === 'warning' ? (
          <NotSupported />
        ) : (
          <DateRangeField
            name={`daterange-${state}`}
            label={'Label'}
            value={values[state]}
            error={state === 'error'}
            disabled={state === 'readonly'}
            onChange={value => setValues(prev => ({...prev, [state]: value}))}
          />
        )
      }
    />
  );
};

const SelectRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, string | undefined>>({
    empty: undefined,
    value: 'nb',
    error: 'nb',
    warning: 'nb',
    readonly: 'nb',
  });
  const options = useMemo(
    () => DEMO_CODES.map(code => ({value: code.code, label: code.value})),
    [],
  );

  return (
    <Row
      title={'Select'}
      render={state =>
        state === 'warning' ? (
          <NotSupported />
        ) : (
          <Select
            value={values[state]}
            options={options}
            error={state === 'error'}
            disabled={state === 'readonly'}
            onChange={value => setValues(prev => ({...prev, [state]: value}))}
          />
        )
      }
    />
  );
};

const CodeListFieldRow: React.FC<{cardinality: 'single' | 'multiple'}> = ({
  cardinality,
}) => {
  const initial = cardinality === 'single' ? 'nb' : ['nb', 'en'];
  const [values, setValues] = useState<
    Record<DemoState, string | string[] | null>
  >({
    empty: cardinality === 'single' ? null : [],
    value: initial,
    error: initial,
    warning: initial,
    readonly: initial,
  });

  return (
    <Row
      title={`CodeListField (${cardinality})`}
      render={state => (
        <CodeListField
          name={`codelist-${cardinality}-${state}`}
          id={`codelist-${cardinality}-${state}`}
          label={'Label'}
          placeholder={'Placeholder'}
          cardinality={cardinality}
          options={DEMO_CODES}
          value={values[state]}
          {...getStateProps(state)}
          onChange={value => setValues(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const RadioButtonsFieldRow: React.FC<{horizontal?: boolean}> = ({
  horizontal,
}) => {
  const [values, setValues] = useState<Record<DemoState, string | null>>({
    empty: null,
    value: 'nb',
    error: 'nb',
    warning: 'nb',
    readonly: 'nb',
  });

  return (
    <Row
      title={`RadioButtonsField${horizontal ? ' (horizontal)' : ''}`}
      render={state => (
        <RadioButtonsField
          name={`radio-${horizontal ? 'h' : 'v'}-${state}`}
          id={`radio-${horizontal ? 'h' : 'v'}-${state}`}
          label={'Label'}
          options={DEMO_CODES.slice(0, 3)}
          horizontal={horizontal}
          value={values[state]}
          {...getStateProps(state)}
          onChange={value => setValues(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const CheckboxFieldRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, boolean>>({
    empty: false,
    value: true,
    error: true,
    warning: true,
    readonly: true,
  });

  return (
    <Row
      title={'CheckboxField'}
      render={state => (
        <CheckboxField
          fieldId={`checkbox-${state}`}
          name={`checkbox-${state}`}
          label={'Label'}
          boxLabel={'Box label'}
          value={values[state]}
          {...getStateProps(state)}
          onChange={value => setValues(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const LinkedFieldRow: React.FC = () => {
  const linkedValue: UnverifiedLinkedValueLink = {
    linkStatus: 'unverified',
    name: 'Jo Nesbø',
  };
  const [links, setLinks] = useState<Record<DemoState, LinkedValueLink>>({
    empty: {linkStatus: 'empty'},
    value: linkedValue,
    error: linkedValue,
    warning: linkedValue,
    readonly: linkedValue,
  });

  return (
    <Row
      title={'LinkedField'}
      render={state => (
        <LinkedField
          name={`linked-${state}`}
          label={'Label'}
          entity={Concept.agent}
          link={links[state]}
          formattedValue={
            'name' in links[state] ? (links[state].name ?? '') : ''
          }
          {...getStateProps(state)}
          onChange={value =>
            setLinks(prev => ({
              ...prev,
              [state]: value ?? {linkStatus: 'empty'},
            }))
          }
          onEdit={value => setLinks(prev => ({...prev, [state]: value}))}
        />
      )}
    />
  );
};

const SingleValueListFieldRow: React.FC = () => {
  const [values, setValues] = useState<Record<DemoState, string[]>>({
    empty: [],
    value: ['Subtitle 1', 'Subtitle 2'],
    error: ['Subtitle 1', 'Subtitle 2'],
    warning: ['Subtitle 1', 'Subtitle 2'],
    readonly: ['Subtitle 1', 'Subtitle 2'],
  });

  const makeListOperations = useCallback(
    (state: DemoState) => {
      const apply = (update: (prev: string[]) => string[]): string[] => {
        const next = update(values[state]);
        setValues(prev => ({...prev, [state]: next}));
        return next;
      };
      return {
        onAppendValue: (prepend?: boolean) =>
          apply(prev => (prepend ? ['', ...prev] : [...prev, ''])),
        onDeleteValue: (index: number) =>
          apply(prev => prev.filter((_, idx) => idx !== index)),
        onMoveValue: (oldIndex: number, newIndex: number) =>
          apply(prev => {
            const next = [...prev];
            const [moved] = next.splice(oldIndex, 1);
            next.splice(newIndex, 0, moved);
            return next;
          }),
        onReplace: (atIdx: number, newValue: string) =>
          apply(prev => prev.map((v, idx) => (idx === atIdx ? newValue : v))),
      };
    },
    [values],
  );

  return (
    <Row
      title={'SingleValueListField'}
      render={state => {
        const {onReplace, ...listOperations} = makeListOperations(state);
        const stateProps = getStateProps(state);
        return (
          <SingleValueListField<string>
            label={'Label'}
            addLabel={'Add value'}
            cardinality={'multiple'}
            values={values[state]}
            {...stateProps}
            errorMessage={
              stateProps.error !== false ? 'Error message' : undefined
            }
            getValueKey={index => `list-${state}-${index}`}
            renderValue={(index, value, id, readonly) => (
              <TextField
                focusableId={id}
                name={`list-${state}-${index}`}
                type={'text'}
                value={value}
                readonly={readonly}
                error={stateProps.error}
                placeholder={'Placeholder'}
                onChange={e => onReplace(index, e.target.value)}
              />
            )}
            {...listOperations}
          />
        );
      }}
    />
  );
};

const BuildingBlocks: React.FC = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID_COLUMNS,
      columnGap: 2,
      rowGap: 2,
      alignItems: 'start',
    }}>
    <Text kind="primary" variant="body2">
      {'FieldLabel'}
    </Text>
    <FieldLabel label={'Plain'} />
    <FieldLabel label={'Required'} required />
    <FieldLabel label={'Error'} error />
    <FieldLabel label={'Warning'} error={'warning'} />
    <Box />

    <Text kind="primary" variant="body2">
      {'GroupLabel'}
    </Text>
    <GroupLabel label={'Group label'} />
    <Box sx={{gridColumn: 'span 4'}} />

    <Text kind="primary" variant="body2">
      {'FieldValue'}
    </Text>
    <FieldValue value={'Readonly field value'} />
    <Box sx={{gridColumn: 'span 4'}} />

    <Text kind="primary" variant="body2">
      {'FieldErrorMessage'}
    </Text>
    <FieldErrorMessage error errorMessage={'Error message'} />
    <FieldErrorMessage error={'warning'} errorMessage={'Warning message'} />
    <Box sx={{gridColumn: 'span 3'}} />

    <Text kind="primary" variant="body2">
      {'EmptyListPlaceholder'}
    </Text>
    <EmptyListPlaceholder />
    <Box sx={{gridColumn: 'span 4'}} />

    <Text kind="primary" variant="body2">
      {'NullFieldPlaceholder'}
    </Text>
    <Box sx={theme => ({border: `1px dashed ${theme.palette.divider}`})}>
      <NullFieldPlaceholder />
    </Box>
    <Box sx={{gridColumn: 'span 4'}} />

    <Text kind="primary" variant="body2">
      {'AddButton'}
    </Text>
    <AddButton label={'Add value'} />
    <Box sx={{gridColumn: 'span 4'}} />
  </Box>
);

export const FieldsDemoScene: React.FC<Props> = () => {
  const LinkedValueSearchContextProvider =
    useLinkedValueSearchContextProvider();

  return (
    <LinkedValueSearchContextProvider>
      <Scene browserTitle={'Fields demo'}>
        <Scene.Header title={'Field components'} />
        <Scene.Content>
          <Card level="level3">
            <Card.Header>
              <Text variant="h2" kind="primary">
                {'Fields by state'}
              </Text>
            </Card.Header>
            <Card.Content>
              <HeaderRow />
              <TextFieldRow type={'text'} />
              <TextFieldRow type={'digits'} />
              <TextAreaFieldRow />
              <HtmlFieldRow />
              <YearPickerFieldRow />
              <DatePickerFieldRow />
              <DateRangeFieldRow />
              <SelectRow />
              <CodeListFieldRow cardinality={'single'} />
              <CodeListFieldRow cardinality={'multiple'} />
              <RadioButtonsFieldRow />
              <RadioButtonsFieldRow horizontal />
              <CheckboxFieldRow />
              <LinkedFieldRow />
              <SingleValueListFieldRow />
            </Card.Content>
          </Card>
          <Spacer size={2} />
          <Card level="level3">
            <Card.Header>
              <Text variant="h2" kind="primary">
                {'Building blocks'}
              </Text>
            </Card.Header>
            <Card.Content>
              <BuildingBlocks />
            </Card.Content>
          </Card>
        </Scene.Content>
      </Scene>
    </LinkedValueSearchContextProvider>
  );
};
